import { useMemo } from 'react';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Text from '../text/Text';
import {
  getCurrentDay,
  getCurrentWeek,
  getMonthNumber,
} from 'src/app/utils/calendar.utils';
import styles from './CalendarSmall.module.css';
import { ParsedDay } from 'src/types/date-time.types';

type CalendarSmallProps = {
  selectedDay: Date;
  attackDays: Date[];
  vyduraPerAttack: Date[];
  vyduraEveryOtherDay: Date[];
  handleSelectDay: (day: Date) => void;
};

const CalendarSmall = ({
  attackDays,
  vyduraPerAttack,
  vyduraEveryOtherDay,
  selectedDay,
  handleSelectDay,
}: CalendarSmallProps) => {
  const today: ParsedDay = useMemo(() => getCurrentDay(new Date()), []);
  const monthOrder = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const selectedDate: ParsedDay = useMemo(
    () => getCurrentDay(selectedDay),
    [selectedDay]
  );
  const calendarDays: ParsedDay[] = useMemo(
    () => getCurrentWeek(new Date()),
    []
  );
  const gridCellStyles = {
    color: 'white',
    width: '40px',
    margin: '0 auto',
  };
  const dayCellStyles = {
    height: '40px',
    lineHeight: 'unset',
    border: '1px solid',
    borderColor: 'transparent',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const checkIfDayIsEventDay = (day: ParsedDay, eventDays: Date[]) => {
    const parsedEventDays = eventDays.map((eventDay) =>
      getCurrentDay(eventDay)
    );
    const match = parsedEventDays.find(
      (eventDay) => eventDay.date === day.date && eventDay.month === day.month
    );
    return Boolean(match);
  };

  return (
    <div className={styles.wrapper}>
      <Row justify='space-between'>
        {calendarDays.map((day) => {
          const isCurrentDay = day.day === today.day;
          const isSelected = day.day === selectedDate.day;
          return (
            <Col span={isCurrentDay ? 4 : 3} key={day.day}>
              <Text
                tag='div'
                centered
                style={{
                  ...gridCellStyles,
                  opacity: isSelected ? 1 : 0.6,
                  paddingBottom: '12px',
                }}
              >
                {isCurrentDay ? 'Today' : day.day}
              </Text>
            </Col>
          );
        })}
      </Row>
      <Row justify='space-between'>
        {calendarDays.map((day) => {
          const isSelected = day.day === selectedDate.day;
          const isCurrentDay = day.day === today.day;
          const isCurrentMonth = day.month === today.month;
          const isFutureDay = isCurrentMonth
            ? day.date > today.date
            : monthOrder.indexOf(day.month) > monthOrder.indexOf(today.month);
          const isAttackDay = checkIfDayIsEventDay(day, attackDays);
          const isMedicationDay = checkIfDayIsEventDay(
            day,
            vyduraEveryOtherDay
          );
          const isVyduraPerAttackDay = checkIfDayIsEventDay(
            day,
            vyduraPerAttack
          );
          const attackStyles = {
            background: 'var(--color-red)',
          };
          const selectedStyles = {
            background: 'var(--color-darken)',
          };
          const selectedWithAttackStyles = {
            borderColor: 'var(--color-darken)',
            borderWidth: '6px',
          };
          let composedStyles = {};
          if (isSelected) {
            composedStyles = isAttackDay
              ? { ...selectedWithAttackStyles, ...attackStyles }
              : selectedStyles;
          } else {
            composedStyles = isAttackDay ? attackStyles : {};
          }
          const dayDate = new Date(
            day.year,
            getMonthNumber(day.month),
            day.date
          );
          return (
            <Col
              span={isCurrentDay ? 4 : 3}
              key={day.date}
              onClick={() =>
                isFutureDay ? () => undefined : handleSelectDay(dayDate)
              }
            >
              <Text
                centered
                tag='div'
                style={{
                  // position: 'relative',
                  cursor: 'pointer',
                  opacity: isFutureDay ? 0.6 : 1,
                  ...gridCellStyles,
                  ...dayCellStyles,
                  ...composedStyles,
                  backgroundClip: 'padding-box',
                  WebkitBackgroundClip: 'padding-box',
                }}
              >
                <span>{day.date}</span>
              </Text>
              {isMedicationDay && (
                <span
                  className={`${styles.medication} ${styles['vydura-every-day']}`}
                />
              )}
              {isVyduraPerAttackDay && (
                <span
                  className={`${styles.medication} ${styles['vydura-per-attack']}`}
                />
              )}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default CalendarSmall;
